import { FC } from "react";
import styles2 from '../page-dashboard-V2/preflight.module.scss';
import { LineChart } from "../../components/shared/Charts/LineChart/LineChart";
import SummaryCard from "../../components/shared/Charts/SummaryCard/SummaryCard";
import PieChart from "../../components/shared/Charts/PieChart/PieChart";
import SummaryBarList from "../../components/shared/Charts/SummaryBarList/SummaryBarList";
import { Color } from "@tremor/react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Stats } from "./page-bid-request-list";

type Props = {
  stats: Stats,
}

const ReceivedBidRequestAllStats: FC<Props> = ({ stats }) => {
  const [t] = useTranslation('requests', { keyPrefix: 'received.statistics' });

  type perWeekLabelsType = { week: string, requests: string, bids: string, requestsSinceAd: string, bidsSinceAd: string };
  const perWeekLabels: perWeekLabelsType = t('perWeek.label', { returnObjects: true }) as perWeekLabelsType;

  const lineChartTitles = [perWeekLabels.requests, perWeekLabels.bids];
  const lineChartColors: Color[] = ['orange', 'cyan',];

  const requestsCardTitle = t('requests.title', 'Förfrågningar totalt', { context: null });
  const bidsCardTitle = t('bids.title', 'Anbud totalt', { context: null });
  const averageWonProcurementsCardTitle = t('averageWonProcurements.title', 'Vunna upphandlingar totalt', { context: null });

  const {
    totalRequests,
    totalBids,
    won,
    ongoing,
    recent,
    older,
    chartData,
    topOrganizations,
  } = stats;

  const chartData2 = chartData.map((w) => {
    return {
      [perWeekLabels.week]: t('week', 'Vecka') + ' ' + w.week,
      [perWeekLabels.requests]: w.requests,
      [perWeekLabels.bids]: w.bids,
      won: w.won,
      isAfterAd: w.isAfterAd,
    }
  });

  return (
    <div style={{
      marginBottom: "16px",
      width: "100%",
      display: "grid",
      gridTemplateColumns: "calc(33% - 16px) calc(33% - 16px) calc(33% - 16px)",
      gridTemplateRows: `128px 1fr 1fr`,
      gap: "16px",
    }} className={styles2.pageDashboardPreflightTailwindStyles}>
      <SummaryCard
        title={requestsCardTitle}
        value={totalRequests}
      />
      <SummaryCard
        title={bidsCardTitle}
        value={totalBids}
      />
      <SummaryCard
        title={averageWonProcurementsCardTitle}
        value={won}
        infoText={t('averageWonProcurements.tooltip', 'Totalt antal inköp där inköparen valt er som vinnande leverantör och signerat kontrakt')}
      />

      <PieChart
        title={t('ongoing.title', 'Pågående förfrågningar')}
        categories={[
          `${t('label.unanswered', 'Obesvarade')}: ${ongoing.ongoingUnanswered}`,
          `${t('label.accepted', 'Avser lämna anbud')}: ${ongoing.ongoingAccepted}`,
          `${t('label.declined', 'Avser inte lämna anbud')}: ${ongoing.ongoingDeclined}`,
          `${t('label.bid', 'Anbud inlämnat')}: ${ongoing.ongoingBids}`,
        ]}
        data={[
          { name: t('label.unanswered', 'Obesvarade'), value: ongoing.ongoingUnanswered },
          { name: t('label.accepted', 'Avser lämna anbud'), value: ongoing.ongoingAccepted },
          { name: t('label.declined', 'Avser inte lämna anbud'), value: ongoing.ongoingDeclined },
          { name: t('label.bid', 'Anbud inlämnat'), value: ongoing.ongoingBids },
        ]}
        colors={['yellow', 'slate', 'rose', 'emerald']}
      />
      <PieChart
        title={t('recent.title', 'Förfrågningar utgångna senaste 30 dagarna')}
        infoText={t('recent.tooltip', 'Förfrågningar där anbudstiden gick ut senaste 30 dagarna')}
        categories={[
          `${t('label.unanswered', 'Obesvarade')}: ${recent.recentUnanswered}`,
          `${t('label.accepted', 'Avser lämna anbud (men inget anbud lämnat)', { context: 'long' })}: ${recent.recentAccepted}`,
          `${t('label.declined', 'Avser inte lämna anbud')}: ${recent.recentDeclined}`,
          `${t('label.bid', 'Anbud inlämnat')}: ${recent.recentBids}`,
        ]}
        data={[
          { name: t('label.unanswered', 'Obesvarade'), value: recent.recentUnanswered },
          { name: t('label.accepted', 'Avser lämna anbud'), value: recent.recentAccepted },
          { name: t('label.declined', 'Avser inte lämna anbud'), value: recent.recentDeclined },
          { name: t('label.bid', 'Anbud inlämnat'), value: recent.recentBids },
        ]}
        colors={['yellow', 'slate', 'rose', 'emerald']}
      />
      <PieChart
        title={t('older.title', 'Äldre förfrågningar')}
        infoText={t('older.tooltip', 'Förfrågningar där anbudstiden har gått ut för mer än 30 dagar sedan')}
        categories={[
          `${t('label.unanswered', 'Obesvarade')}: ${older.olderUnanswered}`,
          `${t('label.accepted', 'Avser lämna anbud (men inget anbud lämnat)', { context: 'long' })}: ${older.olderAccepted}`,
          `${t('label.declined', 'Avser inte lämna anbud')}: ${older.olderDeclined}`,
          `${t('label.bid', 'Anbud inlämnat')}: ${older.olderBids}`,
        ]}
        data={[
          { name: t('label.unanswered', 'Obesvarade'), value: older.olderUnanswered },
          { name: t('label.accepted', 'Avser lämna anbud'), value: older.olderAccepted },
          { name: t('label.declined', 'Avser inte lämna anbud'), value: older.olderDeclined },
          { name: t('label.bid', 'Anbud inlämnat'), value: older.olderBids },
        ]}
        colors={['yellow', 'slate', 'rose', 'emerald']}
      />
      <LineChart
        title={t('perWeek.title', 'Förfrågningar och anbud per vecka')}
        categories={lineChartTitles}
        index={perWeekLabels.week}
        data={chartData2}
        colors={lineChartColors}
        style={{ gridColumnStart: 1, gridColumnEnd: 3 }}
      />
      <SummaryBarList
        title={t('perSupplier.title', 'Förfrågningar per beställare totalt')}
        keyColumnName={t('perSupplier.label.name', 'Namn')}
        valueColumName={t('perSupplier.label.noOfRequests', 'Förfrågningar')}
        data={topOrganizations}
      />
    </div>
  );
}

export default ReceivedBidRequestAllStats;
